/**
 * RoleBadge Component
 * Displays role badges for familiarisation items
 *
 * Roles per WBC3 Table A5.3:
 * - UNIVERSAL (grey): Required for all crew members
 * - DECK (blue): Required for deck officers and deck crew
 * - ENGINEERING (yellow): Required for engineers and engine room crew
 * - CATERING (green): Required for galley staff and food handlers
 */

import React from 'react';
import { Users, Anchor, Settings, UtensilsCrossed } from 'lucide-react';

// Badge configuration for each role type
const ROLE_CONFIG = {
  UNIVERSAL: {
    icon: Users,
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    border: 'border-gray-200',
    label: 'All Crew',
    emoji: '👥',
    tooltip: 'Required for all crew members'
  },
  DECK: {
    icon: Anchor,
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-200',
    label: 'Deck',
    emoji: '⚓',
    tooltip: 'Required for deck officers and deck crew'
  },
  ENGINEERING: {
    icon: Settings,
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-200',
    label: 'Engineering',
    emoji: '⚙️',
    tooltip: 'Required for engineers and engine room crew'
  },
  CATERING: {
    icon: UtensilsCrossed,
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-200',
    label: 'Catering',
    emoji: '🍳',
    tooltip: 'Required for galley staff and food handlers'
  }
};

/**
 * RoleBadge - Badge showing which crew role the item applies to
 *
 * @param {string|string[]} role - UNIVERSAL, DECK, ENGINEERING, CATERING or array of roles
 * @param {boolean} compact - If true, shows abbreviated version
 * @param {boolean} showEmoji - If true, shows emoji (default true)
 */
const RoleBadge = ({
  role,
  compact = false,
  showEmoji = true
}) => {
  // Handle array of roles
  const roles = Array.isArray(role) ? role : [role];

  return (
    <div className="flex gap-1 flex-wrap">
      {roles.map(r => {
        // Get config for this role (default to UNIVERSAL if unknown)
        const config = ROLE_CONFIG[r] || ROLE_CONFIG.UNIVERSAL;
        const { icon: Icon, bg, text, border, label, emoji, tooltip } = config;

        // Compact version - just colored dot
        if (compact) {
          return (
            <span
              key={r}
              className={`inline-block w-2 h-2 rounded-full ${bg.replace('100', '500')}`}
              title={tooltip}
            />
          );
        }

        return (
          <span
            key={r}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${bg} ${text} ${border}`}
            title={tooltip}
          >
            {showEmoji ? (
              <span className="text-[10px]">{emoji}</span>
            ) : (
              <Icon className="w-3 h-3" />
            )}
            <span>{label}</span>
          </span>
        );
      })}
    </div>
  );
};

/**
 * RoleLegend - Shows all role types with descriptions
 * Useful for displaying at the top of familiarisation checklists
 */
export const RoleLegend = ({ className = '' }) => {
  return (
    <div className={`flex flex-wrap gap-3 text-xs ${className}`}>
      {Object.entries(ROLE_CONFIG).map(([key, config]) => {
        return (
          <div
            key={key}
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border ${config.bg} ${config.text} ${config.border}`}
            title={config.tooltip}
          >
            <span>{config.emoji}</span>
            <span className="font-medium">{config.label}</span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * getRoleStats - Helper to calculate role distribution
 * @param {Array} items - Array of familiarisation items with role field
 * @returns {Object} Counts for each role type
 */
export const getRoleStats = (items = []) => {
  const stats = {
    UNIVERSAL: 0,
    DECK: 0,
    ENGINEERING: 0,
    CATERING: 0,
    total: items.length
  };

  items.forEach(item => {
    const roles = Array.isArray(item.role) ? item.role : [item.role || 'UNIVERSAL'];
    roles.forEach(role => {
      if (stats[role] !== undefined) {
        stats[role]++;
      }
    });
  });

  return stats;
};

export default RoleBadge;
